<template>
  <esg-base-chart>
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <apex-chart type="bar"
                ref="chart"
                :width=this.width
                :height=this.height
                v-show="!loading"
                :options="chartOptions"
                :series="series"/>
  </esg-base-chart>
</template>

<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  components: { EsgBaseChart },
  props: {
    corporation: {
      type: Object,
      required: true,
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '250'
    },
    legend: {
      type: String,
      default: 'bottom'
    },
    sort: {
      type: String,
      required: false,
      default: 'dsc'
    }
  },
  watch: {
    corporation: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchData();
      }
    },
    sort: {
      immediate: true,
      handler() {
        this.sortSeries(this.sort);
      }
    }
  },
  data() {
    return {
      loading: false,
      businessLocations: [],
      ascLabels: [],
      dscLabels: [],
      ascSeries: [],
      dscSeries: [],
      series: [],
      chartOptions: {
        chart: {
          type: "bar",
          stacked: true,
          stackType: "normal",
          fontFamily: 'NanumGothic, serif',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            horizontal: false,
            dataLabels: {
              position: 'top',
              total: {
                enabled: true,
                formatter: val => this.formatNumber(val, 0),
                style: {
                  fontFamily: 'NanumGothic',
                  fontWeight: 'normal',
                  colors: ['#000']
                },
              }
            }
          },
        },
        xaxis: {
          categories: [],
          crosshairs: { show: false }
        },
        dataLabels: { enabled: false, },
        yaxis: {
          show: true,
          title: {
            text: '탄소 배출량 (tCO2)',
            style: {
              fontSize: '12px',
              fontWeight: 'normal',
            },
          },
          labels: {
            formatter: val => this.compactFormatNumber(val),
            minWidth: 30,
          },
          crosshair: { show: false }
        },
        tooltip: {
          y: {
            formatter: val => this.formatNumber(val, 2) + ' tCO2'
          }
        },
        grid: { show: false },
        legend: { position: this.legend },
      },
    }
  },
  methods: {
    async fetchData() {
      if (!this.corporation || !this.corporation.code) return;

      try {
        this.loading = true;

        // Fetch corporations
        this.abortController = new AbortController();
        let response = await axios.get('esg/lca/business-location', {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal,
          params: {
            filters: [
              {
                name: 'esg_base_corporation.tenant_id',
                value: this.tenantId(),
                operation: 'EQUAL'
              },
              {
                name: 'esg_base_corporation.code',
                value: this.corporation.code,
                operation: 'EQUAL'
              }
            ]
          }
        });
        this.businessLocations = response.data.list;

        // Fetch chart data
        let year = new Date().getFullYear();
        let years = [year];
        let scopes = [1, 2, 3]
        let args = [{
          years,
          scopes,
          groupKeys: ['year', 'scope', 'business_location_code']
        }];

        let data = new FormData()
        data.append('path', 'esg/ghg_emission/main');
        data.append('json', JSON.stringify(args));

        response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }

        let list = JSON.parse(response.data.data)[0].data;

        this.setTotal(list, this.businessLocations);
        this.setSeriesAndLabels(list, scopes, this.businessLocations);
        this.sortSeries(this.sort);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    setTotal(list, businessLocations) {
      businessLocations.forEach(bizLoc => {
        bizLoc.total = list.filter(row => row.business_location_code === bizLoc.business_location_code)
            .reduce((prev, row) => prev + row.values, 0);
      });
    },
    setSeriesAndLabels(list, scopes, businessLocations) {
      // Sor corporations in ascending order of the total
      businessLocations.sort((a, b) => a.total - b.total);

      // Set labels for each order, i.e. ascending and descending
      this.ascLabels = businessLocations.map(loc => loc.name);
      this.dscLabels = structuredClone(this.ascLabels);
      this.dscLabels.reverse();

      // Set series
      let series = [];
      scopes.forEach(scope => {
        let data = [];
        businessLocations.forEach(bizLoc => {
          let row = list.find(row => row.business_location_code === bizLoc.business_location_code && row.scope === scope);
          data.push(row ? row.values : 0);
        });
        series.push({
          name: 'Scope ' + scope,
          type: 'column',
          data
        });
      });
      this.ascSeries = series;
      this.dscSeries = structuredClone(this.ascSeries);
      this.dscSeries.forEach(series => series.data.reverse());
    },
    sortSeries(sort) {
      if (sort === 'dsc') {
        this.series = this.dscSeries
        this.chartOptions.xaxis.categories = this.dscLabels;
      } else {
        this.series = this.ascSeries;
        this.chartOptions.xaxis.categories = this.ascLabels;
      }
      this.refreshChart();
    }
  },
}
</script>